import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { DisplayAd } from "@/components/DisplayAd";
import type { EarningApp } from "@/lib/earning-apps/apps";
import { EARNING_APP_BY_SLUG } from "@/lib/earning-apps/apps";
import { getAppReviews, averageStars, getHinglishOverview } from "@/lib/earning-apps/reviews";
import { UserReviewsSection } from "@/components/earning-apps/UserReviews";
import { EarningAppVideo } from "./EarningAppVideo";
import type { VideoConfig } from "@/lib/video-embeds";


export function EarningAppShell({ app, videos = [] }: { app: EarningApp; videos?: (VideoConfig & { slot: number; intro: string })[] }) {
  const reviews = getAppReviews(app);
  const avg = averageStars(reviews);
  const [hinglish, setHinglish] = useState(false);
  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:text-primary">Home</Link>
          {" / "}
          <Link to="/earning-apps-review" className="hover:text-primary">Earning Apps Review</Link>
          {" / "}
          <span className="text-foreground">{app.name}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">
          {app.name} — Real Or Fake? Honest {app.category} Review
        </h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          A first-hand review of {app.name}: how the {app.category.toLowerCase()} model actually pays, the specific
          red flags DrHint verified, and whether it belongs on your phone in {new Date().getFullYear()}.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-2xl bg-muted/50 px-3 py-1.5 text-xs font-semibold">
            <StarRow value={avg} />
            <span>{avg.toFixed(1)}</span>
            <span className="text-muted-foreground">· {reviews.length} user reviews</span>
          </div>
          <button
            type="button"
            onClick={() => setHinglish((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-2xl bg-primary/10 text-primary hover:bg-primary/20 px-3 py-1.5 text-xs font-bold ring-1 ring-primary/30 transition-colors"
            aria-pressed={hinglish}
          >
            {hinglish ? "🇬🇧 English Summary" : "🇮🇳🇵🇰 Hinglish Summary"}
          </button>
        </div>

        {hinglish && (
          <div className="mt-4 rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-primary mb-2">Hinglish mein samjho</div>
            <div className="text-[15px] leading-relaxed whitespace-pre-line text-foreground/90">
              {getHinglishOverview(app)}
            </div>
          </div>
        )}
      </section>

      {/* Snapshot fact table — per-app unique data */}
      <Section title="Quick facts">
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm">
            <tbody className="[&_td]:px-4 [&_td]:py-2.5 [&_tr]:border-b [&_tr:last-child]:border-b-0">
              <tr><td className="font-semibold w-56">Owner company</td><td>{app.ownerCompany}</td></tr>
              <tr><td className="font-semibold">Headquarters</td><td>{app.hq}</td></tr>
              <tr><td className="font-semibold">Founded</td><td>{app.founded} · {app.yearsOperational} years operational</td></tr>
              <tr><td className="font-semibold">Category</td><td>{app.category}</td></tr>
              <tr><td className="font-semibold">Available in</td><td>{app.regions.join(", ")}</td></tr>
              <tr><td className="font-semibold">Minimum payout</td><td>{app.minPayout}</td></tr>
              <tr><td className="font-semibold">Payout methods</td><td>{app.payoutMethods.join(" · ")}</td></tr>
              <tr><td className="font-semibold">Realistic hourly</td><td>{app.earningsPerHour}</td></tr>
            </tbody>
          </table>
        </div>
      </Section>

      {videos.length > 0 && (
        <Section title={`${app.name} video review`}>
          <div className="space-y-6">
            {videos.map((video) => <article key={`${app.slug}-${video.slot}`} aria-label={video.name} className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
              {video.intro.trim() && <p className="whitespace-pre-line break-words px-4 py-5 text-[15px] leading-relaxed text-card-foreground sm:px-6">{video.intro}</p>}
              <EarningAppVideo key={video.videoId} videoId={video.videoId} title={video.name} />
            </article>)}
          </div>
        </Section>
      )}

      <Section title={`How ${app.name} actually pays you`}>
        <p>{app.mechanism}</p>
        <p className="mt-3 text-sm text-muted-foreground">
          The mechanism above matters because it explains where the money originates. If a company can't
          answer "who buys what my labour / bandwidth / data produces?" then the payout model is
          structurally unsound. For {app.name}, the answer is clearly defined — which is why it earns a
          <b> {app.verdict}</b> verdict from DrHint.
        </p>
      </Section>

      <div className="mx-auto max-w-4xl px-4">
        <DisplayAd placement="inline" className="my-8" />
      </div>

      <Section title={`Real signals — why we believe ${app.name} pays`}>
        <ul className="list-disc pl-5 space-y-2">
          {app.realSignals.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      </Section>

      <Section title={`Red flags to know before installing ${app.name}`}>
        <ul className="list-disc pl-5 space-y-2">
          {app.redFlags.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      </Section>

      <Section title="DrHint's payment-proof test">
        <blockquote className="border-l-4 border-primary/60 bg-muted/40 rounded-r-xl px-4 py-3 italic">
          {app.paymentProofNote}
        </blockquote>
      </Section>

      <Section title="Who this app is (and isn't) for">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-emerald-500 mb-1">Best for</div>
            <p className="text-sm">{app.bestFor}</p>
          </div>
          <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-red-500 mb-1">Worst for</div>
            <p className="text-sm">{app.worstFor}</p>
          </div>
        </div>
      </Section>

      <Section title="Reviewer's take">
        <p className="text-[15px]">
          <b>DrHint editorial verdict:</b> {app.reviewerQuote}
        </p>
      </Section>

      <UserReviewsSection slug={app.slug} appName={app.name} editorial={reviews} />

      <Section title={`Alternatives to ${app.name} worth comparing`}>
        <div className="grid gap-2 sm:grid-cols-2">
          {app.competitorSlugs
            .map((s) => EARNING_APP_BY_SLUG[s])
            .filter(Boolean)
            .map((alt) => (
              <Link
                key={alt.slug}
                to="/earning-apps-review/$slug"
                params={{ slug: alt.slug }}
                className="block rounded-2xl border border-border/60 bg-card/50 p-4 hover:border-primary/50 hover:bg-primary/5 transition-colors"
              >
                <div className="text-sm font-bold">{alt.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{alt.category} · {alt.verdict}</div>
              </Link>
            ))}
        </div>
      </Section>

      <Section title="Frequently asked questions">
        <div className="space-y-3">
          {app.faqExtra.map((f, i) => (
            <details key={i} className="rounded-xl border border-border bg-card p-4">
              <summary className="font-semibold cursor-pointer">{f.q}</summary>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
          <details className="rounded-xl border border-border bg-card p-4">
            <summary className="font-semibold cursor-pointer">Is {app.name} legit?</summary>
            <p className="mt-2 text-sm text-muted-foreground">
              Based on ownership ({app.ownerCompany}), {app.yearsOperational} years of continuous operation,
              and the payment-proof test above, DrHint rates {app.name} as <b>{app.verdict}</b>.
            </p>
          </details>
          <details className="rounded-xl border border-border bg-card p-4">
            <summary className="font-semibold cursor-pointer">How much can I realistically earn from {app.name}?</summary>
            <p className="mt-2 text-sm text-muted-foreground">
              Honest ceiling based on our tests: {app.earningsPerHour}. Anyone promising 10× that number is
              running an affiliate funnel — not describing the app's real economics.
            </p>
          </details>
        </div>
      </Section>

      <Section title="Disclaimer & how we review">
        <p className="text-sm text-muted-foreground">
          DrHint reviews each earning app by (1) creating a real account, (2) actively using the app for
          at least 30 days on a documented device, (3) attempting a full withdrawal cycle to the advertised
          payout method, and (4) cross-checking Trustpilot, Reddit r/beermoney and Play Store review
          histories. We do not accept payment from the reviewed apps. Verdicts may change as the app's
          ownership or payout policy changes — this review reflects the state on the last-updated date below.
        </p>
      </Section>

      <section className="mx-auto max-w-3xl px-4 mt-8 mb-16">
        <div className="rounded-2xl border border-border bg-muted/30 p-4 text-xs text-muted-foreground">
          <div><strong>Reviewed by:</strong> DrHint Editorial · Earning-app research desk</div>
          <div className="mt-1">
            Contact: hello@drhint.com ·
            {" "}<Link to="/earning-apps-review" className="underline">Full Earning Apps Review directory</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-3xl px-4 mt-10">
      <h2 className="text-xl md:text-2xl font-bold mb-3">{title}</h2>
      <div className="text-[15px] leading-relaxed text-foreground/90">{children}</div>
    </section>
  );
}

function StarRow({ value }: { value: number }) {
  const full = Math.round(value);
  return (
    <span className="inline-flex items-center gap-0.5 text-amber-400" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} viewBox="0 0 20 20" className="w-3.5 h-3.5" fill={i <= full ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5}>
          <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.9L10 15l-5.2 2.8 1-5.9L1.5 7.7l5.9-.9z" />
        </svg>
      ))}
    </span>
  );
}

export function buildEarningAppLd(app: EarningApp) {
  const url = `https://drhint.com/earning-apps-review/${app.slug}`;
  const reviews = getAppReviews(app);
  const ratingValue = reviews.length > 0 ? averageStars(reviews) : null;

  const review = {
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: {
      "@type": "SoftwareApplication",
      name: app.name,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Android / iOS / Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      ...(ratingValue !== null
        ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue,
              reviewCount: reviews.length,
              bestRating: 5,
              worstRating: 1,
            },
          }
        : {}),
    },
    ...(ratingValue !== null
      ? {
          reviewRating: {
            "@type": "Rating",
            ratingValue,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
    author: { "@type": "Organization", name: "DrHint", url: "https://drhint.com/" },
    publisher: { "@type": "Organization", name: "DrHint", url: "https://drhint.com/" },
    reviewBody: app.reviewerQuote,
    url,
  };

  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      ...app.faqExtra.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
      {
        "@type": "Question",
        name: `Is ${app.name} legit?`,
        acceptedAnswer: { "@type": "Answer", text: `Based on ${app.yearsOperational} years of operation under ${app.ownerCompany}, DrHint rates ${app.name} as ${app.verdict}.` },
      },
    ],
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://drhint.com/" },
      { "@type": "ListItem", position: 2, name: "Earning Apps Review", item: "https://drhint.com/earning-apps-review" },
      { "@type": "ListItem", position: 3, name: app.name, item: url },
    ],
  };

  return [
    { type: "application/ld+json" as const, children: JSON.stringify(review) },
    { type: "application/ld+json" as const, children: JSON.stringify(faq) },
    { type: "application/ld+json" as const, children: JSON.stringify(breadcrumb) },
  ];
}